import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const GlassInput = ({ label, description, error, disabled, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-white/70 mb-2">{label}</label>}
    <input
      {...props}
      disabled={disabled}
      className={`w-full px-4 py-3 rounded-xl text-white placeholder:text-white/30 text-sm transition-all duration-300 focus:outline-none ${
        disabled
          ? 'bg-white/[0.03] border border-white/[0.06] cursor-not-allowed opacity-70'
          : 'bg-white/[0.05] border border-[rgba(254,131,56,0.15)] focus:border-[rgba(254,131,56,0.4)] focus:shadow-[0_0_20px_rgba(254,131,56,0.1)]'
      } ${error ? 'border-red-500/50' : ''}`}
      style={{ backdropFilter: 'blur(10px)' }}
    />
    {description && <p className="text-xs text-white/30 mt-1.5">{description}</p>}
    {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
  </div>
);

const GlassButton = ({ children, variant = 'primary', loading, disabled, icon, onClick, className = '' }) => {
  const base = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer';
  const variants = {
    primary: 'glassmorphic-button text-white hover:scale-[1.02]',
    outline: 'bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.08]',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
};

const ProfileSection = () => {
  const { user, userProfile } = useAuth();
  const [formData, setFormData] = useState({ firstName: '', lastName: '', username: '', email: '', bio: '' });
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!userProfile);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile?.first_name || '',
        lastName: userProfile?.last_name || '',
        username: userProfile?.username || '',
        email: userProfile?.email || '',
        bio: userProfile?.bio || ''
      });
      setAvatarUrl(userProfile?.avatar_url || '');
      setIsLoading(false);
    }
  }, [userProfile]);

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const removeAvatarFile = () => {
    setAvatarFile(null);
    if (avatarPreview) { URL.revokeObjectURL(avatarPreview); setAvatarPreview(null); }
  };

  const noSpaceFields = ['firstName', 'lastName', 'username', 'email'];
  const handleKeyDown = (e) => { if (e.key === ' ' && noSpaceFields.includes(e.target.name)) e.preventDefault(); };
  const handleBeforeInput = (e) => { if (noSpaceFields.includes(e.target.name) && e.data && /\s/.test(e.data)) e.preventDefault(); };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    let sanitizedValue;
    if (name === 'username') {
      sanitizedValue = value.replace(/\s/g, '');
    } else if (noSpaceFields.includes(name)) {
      sanitizedValue = value.replace(/\s/g, '');
    } else {
      sanitizedValue = value;
    }
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData?.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9._]+$/.test(formData?.username?.trim())) {
      newErrors.username = 'Only a-z, 0-9, . and _ are allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    // Auto-lowercase before save
    const cleanUsername = formData?.username?.trim().toLowerCase();
    setFormData(prev => ({ ...prev, username: cleanUsername }));
    setIsSaving(true);
    try {
      let newAvatarUrl = avatarUrl;
      if (avatarFile) {
        const fileExt = avatarFile.name.split('.').pop();
        const filePath = `${user?.id}/${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, avatarFile, { upsert: true });
        if (uploadError) { setErrors({ general: uploadError?.message || 'Failed to upload avatar.' }); setIsSaving(false); return; }
        const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
        newAvatarUrl = publicUrlData?.publicUrl || newAvatarUrl;
      }

      // เช็ค username ซ้ำ (ถ้ามีการเปลี่ยน)
      if (cleanUsername !== userProfile?.username) {
        const { data: existing } = await supabase.rpc('get_email_by_username', {
          p_username: cleanUsername,
        });
        if (existing) {
          setErrors({ username: 'Username already taken' });
          setIsSaving(false);
          return;
        }
      }

      // Update profile via direct supabase call (avoid service layer throw)
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          first_name: formData?.firstName,
          last_name: formData?.lastName,
          username: cleanUsername,
          bio: formData?.bio,
          avatar_url: newAvatarUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user?.id)
        .select()
        .single();

      if (error) {
        console.error('Profile update error:', error);
        setErrors({ general: error?.message || 'Failed to update profile. Please try again.' });
      } else {
        setAvatarUrl(newAvatarUrl);
        setAvatarFile(null);
        if (avatarPreview) {
          URL.revokeObjectURL(avatarPreview);
          setAvatarPreview(null);
        }
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Profile save error:', error);
      setErrors({ general: error?.message || 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (userProfile) {
      setFormData({ firstName: userProfile?.first_name || '', lastName: userProfile?.last_name || '', username: userProfile?.username || '', email: userProfile?.email || '', bio: userProfile?.bio || '' });
      setAvatarUrl(userProfile?.avatar_url || '');
    }
    removeAvatarFile(); setErrors({}); setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="glassmorphic-card rounded-2xl p-8 min-h-[500px]">
        <div className="flex items-center justify-center py-12">
          <div className="text-white/40">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-bold text-white">Profile Information</h2>
        {!isEditing && <GlassButton icon="Edit" onClick={() => setIsEditing(true)}>Edit Profile</GlassButton>}
      </div>

      <div className="space-y-6">
        {errors?.general && (
          <div className="p-3 rounded-xl border border-red-500/30" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
            <p className="text-sm text-red-400">{errors?.general}</p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-white mb-3">Profile Photo</h3>
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              {avatarPreview || avatarUrl ? (
                <img src={avatarPreview || avatarUrl} alt="Avatar" className="w-20 h-20 rounded-full object-cover" style={{ border: '2px solid rgba(254, 131, 56, 0.3)' }} />
              ) : (
                <div className="w-20 h-20 rounded-full flex items-center justify-center font-semibold text-xl text-white" style={{ background: 'linear-gradient(135deg, rgba(254, 131, 56, 0.3), rgba(236, 103, 30, 0.2))', border: '2px solid rgba(254, 131, 56, 0.3)' }}>
                  {formData.firstName?.[0] || ''}{formData.lastName?.[0] || ''}
                </div>
              )}
              {isEditing && (
                <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60 cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
                  <Icon name="Camera" size={20} className="text-white" />
                  <input type="file" accept="image/*" onChange={handleAvatarFileChange} className="hidden" />
                </label>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <p className="text-xs text-white/50">Upload a profile picture. Recommended size: 256x256px</p>
              <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all w-fit ${isEditing ? 'cursor-pointer hover:border-orange-500/40' : 'cursor-default opacity-50'}`}
                style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                <Icon name="Upload" size={14} className="text-white/70" />
                Upload Photo
                <input type="file" accept="image/*" onChange={handleAvatarFileChange} className="hidden" disabled={!isEditing} />
              </label>
              {isEditing && avatarFile && (
                <>
                  <span className="text-xs text-white/40 truncate max-w-[200px]">{avatarFile.name}</span>
                  <button type="button" onClick={removeAvatarFile} className="text-xs text-red-400 hover:underline text-left">Remove selected file</button>
                </>
              )}
              {isEditing && (avatarUrl || avatarPreview) && !avatarFile && (
                <button type="button" onClick={() => { removeAvatarFile(); setAvatarUrl(''); }} className="text-xs text-red-400 hover:underline text-left">Remove avatar</button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <GlassInput label="First Name" type="text" name="firstName" value={formData?.firstName} onChange={handleChange} onKeyDown={handleKeyDown} onBeforeInput={handleBeforeInput} error={errors?.firstName} disabled={!isEditing} placeholder="Enter your first name" />
          <GlassInput label="Last Name" type="text" name="lastName" value={formData?.lastName} onChange={handleChange} onKeyDown={handleKeyDown} onBeforeInput={handleBeforeInput} error={errors?.lastName} disabled={!isEditing} placeholder="Enter your last name" />
        </div>

        <GlassInput
          label="Username"
          type="text"
          name="username"
          value={formData?.username}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBeforeInput={handleBeforeInput}
          error={errors?.username}
          disabled={!isEditing}
          placeholder="Enter your username" />

        {userProfile?.subscription_tier !== 'team_member' && (
          <GlassInput
            label="Email Address"
            type="text"
            name="email"
            value={formData?.email}
            disabled={true}
            description="Email address cannot be changed"
            placeholder="your.email@example.com" />
        )}


        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">Bio</label>
          <textarea
            name="bio" value={formData?.bio} onChange={handleChange} disabled={!isEditing} rows={4}
            className={`w-full px-4 py-3 rounded-xl text-white placeholder:text-white/30 text-sm resize-none transition-all duration-300 focus:outline-none ${
              !isEditing ? 'bg-white/[0.03] border border-white/[0.06] cursor-not-allowed opacity-70' : 'bg-white/[0.05] border border-[rgba(254,131,56,0.15)] focus:border-[rgba(254,131,56,0.4)] focus:shadow-[0_0_20px_rgba(254,131,56,0.1)]'
            }`}
            style={{ backdropFilter: 'blur(10px)' }}
            placeholder="Tell us about your design experience and specializations"
          />
          <p className="text-xs text-white/30 mt-1.5">{formData?.bio?.length}/500 characters</p>
        </div>

        {isEditing && (
          <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <GlassButton icon="Save" loading={isSaving} onClick={handleSave}>Save Changes</GlassButton>
            <GlassButton variant="outline" onClick={handleCancel} disabled={isSaving}>Cancel</GlassButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;
